import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { authService } from "../services/auth";
import { apiOld } from "../services/api";
import { toast } from "sonner";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/Avatar";
import { Separator } from "../components/ui/Separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/AlertDialog";
import {
  User,
  LogOut,
  Camera,
  Save
} from "lucide-react";

interface PerfilPageProps {
  onNavigate?: (page: string) => void;
  user?: any;
}

export function PerfilPage({ onNavigate, user }: PerfilPageProps = {}) {
  const context = useOutletContext<{ user: any; refetchUser: () => Promise<void> }>() || {};
  const currentUser = user || context.user;
  const refetchUser = context.refetchUser;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [profileData, setProfileData] = useState({
    name: "",
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    password: "",
    password_confirmation: "",
  });

  useEffect(() => {
    if (currentUser) {
      setProfileData({
        name: currentUser.name || "",
      });
    }
  }, [currentUser]);

  const handleLogoutConfirm = async () => {
    setShowLogoutDialog(false);
    try {
      // 1. Logout na API nova (Sanctum/token)
      await authService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    }

    try {
      // 2. Logout no fcadmin legado (invalida sessão clássica)
      await apiOld.get('/sair');
    } catch (error) {
      console.error("Erro ao fazer logout no admin legado:", error);
    }

    localStorage.removeItem('sanctum_token');
    window.location.reload();
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let updatedAny = false;

      // 1. Atualizar informações pessoais se tiverem mudado (apenas nome)
      if (profileData.name !== (currentUser?.name || "")) {
        if (!profileData.name.trim()) {
          toast.error("Nome é obrigatório.");
          setIsSubmitting(false);
          return;
        }
        await authService.updateProfile(profileData);
        if (refetchUser) {
          await refetchUser();
        }
        toast.success("Nome de perfil updatedAny com sucesso!");
        updatedAny = true;
      }

      // 2. Atualizar senha se houver campos preenchidos
      if (passwordData.current_password || passwordData.password || passwordData.password_confirmation) {
        if (!passwordData.current_password) {
          toast.error("Por favor, digite sua senha atual para alteração de senha.");
          setIsSubmitting(false);
          return;
        }
        if (passwordData.password !== passwordData.password_confirmation) {
          toast.error("A nova senha e a confirmação não coincidem.");
          setIsSubmitting(false);
          return;
        }
        await authService.changePassword(passwordData);
        toast.success("Senha alterada com sucesso!");
        setPasswordData({
          current_password: "",
          password: "",
          password_confirmation: "",
        });
        updatedAny = true;
      }

      if (!updatedAny) {
        toast.info("Nenhuma alteração detectada.");
      }
    } catch (error: any) {
      console.error("Erro ao salvar perfil:", error);
      const message = error.response?.data?.message || "Erro ao atualizar informações.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="flex-1 p-6 bg-gray-50">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#68686A]">Meu Perfil</h1>
        <p className="text-[#68686A]">Gerencie suas informações pessoais e preferências</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna Esquerda - Informações do Perfil */}
        <div className="lg:col-span-1">
          <Card className="border border-gray-200">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center">
                <div className="relative">
                  <Avatar className="h-24 w-24">
                    <AvatarImage src="/placeholder-avatar.jpg" />
                    <AvatarFallback className="bg-[#D4C070] text-white text-2xl">
                      {currentUser?.name?.charAt(0).toUpperCase() || <User className="h-12 w-12" />}
                    </AvatarFallback>
                  </Avatar>
                  <Button
                    size="icon"
                    className="absolute bottom-0 right-0 h-8 w-8 rounded-full bg-[#D4C070] hover:bg-[#849D70]"
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                </div>

                <h3 className="mt-4 text-xl font-semibold text-gray-900">{currentUser?.name || "Sem Nome Definido"}</h3>
                <p className="text-sm text-[#68686A] mt-1">{currentUser?.email || "Sem Email Definido"}</p>

                <Separator className="my-4" />

                <Button
                  variant="destructive"
                  className="w-full mt-6 bg-[#C2452D] hover:bg-[#A33B25]"
                  onClick={() => setShowLogoutDialog(true)}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sair da Conta
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Coluna Direita - Formulário de Edição */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSave}>
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-[#68686A]">Informações Pessoais</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nome">Nome Completo</Label>
                    <Input
                      id="nome"
                      value={profileData.name}
                      onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">E-mail</Label>
                    <Input
                      id="email"
                      type="email"
                      value={currentUser?.email || ""}
                      disabled
                      className="border-gray-300 bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                </div>


                <Separator />

                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Alterar Senha</h4>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="senha-atual">Senha Atual</Label>
                      <Input
                        id="senha-atual"
                        type="password"
                        placeholder="Digite sua senha atual"
                        className="border-gray-300"
                        value={passwordData.current_password}
                        onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="nova-senha">Nova Senha</Label>
                        <Input
                          id="nova-senha"
                          type="password"
                          placeholder="Digite a nova senha"
                          className="border-gray-300"
                          value={passwordData.password}
                          onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="confirmar-senha">Confirmar Senha</Label>
                        <Input
                          id="confirmar-senha"
                          type="password"
                          placeholder="Confirme a nova senha"
                          className="border-gray-300"
                          value={passwordData.password_confirmation}
                          onChange={(e) => setPasswordData({ ...passwordData, password_confirmation: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    className="bg-[#D4C070] hover:bg-[#849D70] text-white"
                    disabled={isSubmitting}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isSubmitting ? "Salvando..." : "Salvar Alterações"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-gray-300 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070]"
                    onClick={() => onNavigate?.("dashboard")}
                  >
                    Cancelar
                  </Button>
                </div>
              </CardContent>
            </Card>
          </form>
        </div>
      </div>

      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#68686A]">Confirmar Saída</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600">
              Tem certeza que deseja sair da sua conta? Você precisará fazer login novamente para acessar o painel.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 text-gray-700 hover:bg-gray-100">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLogoutConfirm}
              className="bg-[#C2452D] hover:bg-[#A33B25] text-white"
            >
              Sair
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}

export default PerfilPage;
